/* eslint-disable brace-style */
/* eslint-disable no-console */
/* eslint-disable max-depth */
import { get, merge } from 'lodash';
import { select } from 'd3-selection';

import Grid from './Grid';
import AxisNumber from './axes/AxisNumber';
import AxisDate from './axes/AxisDate';
import Indicators from './indicators/indicators';
import IndicatorFlag from './flags/IndicatorFlag';
import DividendEvent from './events/DividendEvent';
import DividendCompleteEvent from './events/DividendCompleteEvent';
import SplitsEvent from './events/SplitsEvent';
import EarningsEvent from './events/EarningsEvent';
import DividendCustomEvent from './events/DividendCustomEvent';
import SplitsCustomEvent from './events/SplitsCustomEvent';
import EarningsCustomEvent from './events/EarningsCustomEvent';
import AnnouncedEarningsEvent from './events/AnnouncedEarningsEvent';
import CustomEvent from './events/CustomEvent';
import ExtendedHoursMarker from './markers/ExtendedHoursMarker';
import { types as eventTypes } from './enums/event';
import subpixel from './util/canvasSubpixelOffset';
import { updateComponentStyles } from './util/style.js';
import createUid from './util/uid';

/**
 * Panels are related groups of axes, indicators and markers on a shared pane.
 * Multiple panels can be stacked vertically and are managed by the parent chart.
 */
export default class Panel {
	constructor() {
		return;
	}
	/**
	 * initialize the fundamental collections of params, scales and indicators for a single panel
	 * initialize the canvas dom element
	 */
	init(core, params = {}) {
		this.core = core;

		// then merge with any custom params that were passed to init
		this.params = merge({}, this.getDefaultParams(), params);

		// unique id
		this.params.uid = 'uid' in params ? params.uid : createUid();

		// deprecated id, here for backward compatibility
		this.params.id = this.params.uid;

		// Use this.size collection internally instead of modifying the original params
		this.initPanelSize();

		// grid functionality
		this.grid = new Grid(this);

		// Set up important collections
		this.indicators = [];
		this.events = [];
		this.tools = [];
		this.flags = {};

		// Set up axes
		const start = this.size.padding.left;
		const stop = start + this.size.width;
		this.xAxis = new AxisDate(this, { range: [start, stop] });
		this.yAxis = new AxisNumber(this);

		// Set up important DOM nodes

		// Create container for all panel nodes
		this.rootPanel = this.core.rootModchart
			.insert('div', 'div.modcharts-rootmouse')
			.attr('class', 'modcharts-panel')
			.classed('modcharts-panel-first', this.isUpper === true)
			.attr('uid', this.params.uid)
			.style('width', '100%')
			.style('position', 'absolute');

		// use fixed or dynamic height
		this.size.height = this.calculatedHeight;

		// container for panel legend
		this.rootLegend = this.rootPanel
			.append('div')
			.attr('class', `modcharts-legend${(this.index === 0 ? ' first' : '')}`)
			.style('position', 'absolute')
			.on('mouseup', event => {
				this.core.onMouseup(event);
			});

		// main panel drawing canvas
		this.rootCanvas = this.rootPanel
			.append('canvas')
			.attr('aria-hidden', true)
			.attr('width', this.size.width)
			.attr('height', this.size.height)
			.style('position', 'absolute')
			.attr('class', 'modcharts-panel-root');

		// tool canvas
		this.rootTools = this.rootPanel
			.append('canvas')
			.attr('aria-hidden', true)
			.attr('width', this.size.width)
			.attr('height', this.size.height)
			.style('position', 'absolute')
			.attr('class', 'modcharts-panel-tools');

		// crosshair/overlay root svg
		this.rootOverlay = this.rootPanel
			.append('svg')
			.attr('aria-hidden', true)
			.attr('width', this.core.size.width)
			.attr('height', this.size.height)
			.style('position', 'absolute')
			.attr('class', 'modcharts-rootoverlay')
			.append('g');

		// error message
		this.rootError = this.rootPanel
			.append('div')
			.style('position', 'absolute')
			.style('display', 'none')
			.attr('class', 'modcharts-panel-error');

		// save reference to frequently-used contexts
		this.rootContext = this.getContext(this.rootCanvas.node());
		this.rootToolContext = this.getToolContext(this.rootTools.node());

		return this;
	}

	/**
	 * return main panel canvas context
	 */
	getContext(rootCanvasNode) {
		const context = rootCanvasNode.getContext('2d');

		if (!context.setLineDash) {
			context.setLineDash = () => {
				return;
			};
		}

		return context;
	}

	/**
	 * return tool canvas context
	 */
	getToolContext(rootToolsNode) {
		const context = rootToolsNode.getContext('2d');

		if (!context.setLineDash) {
			context.setLineDash = () => {
				return;
			};
		}

		return context;
	}

	/** default panel param collection.  any custom user params will be merged into this base collection. */
	getDefaultParams() {
		let panelStyle = this.core.getStyle('panel');
		// alternate styling for lower panels
		if (!this.isUpper) {
			panelStyle = merge(panelStyle, this.core.getStyle('panelLower'));
		}
		// alternate styling for left-hand YAxis panels
		if (this.core.params.yAxisSide === 'left') {
			panelStyle = merge(panelStyle, this.core.getStyle('panelYAxisLeft'));
		}

		return {
			enableCrosshair: true, // Use for per panel disabling of crosshair
			hasXAxis: null,
			style: panelStyle,
			size: {
				heightPct: null,
				heightToChartWidthRatio: null
			},
			yAxis: {
				format: 'default',
				range: null, // custom axis range. ex: [-100, 100]
				rangeValues: []
			}
		};
	}

	/** Remove root panel children and then the root panel itself */
	remove() {
		const panelNode = this.rootPanel.node();
		// Remove all children from the panel
		while (panelNode.firstChild) {
			panelNode.removeChild(panelNode.firstChild);
		}
		if (panelNode && panelNode.parentNode) {
			// Remove the panel from the chart
			panelNode.parentNode.removeChild(panelNode);
		}
	}

	/**
	 * add a new Indicator object to the indicators collection
	 * @param {string} id
	 * @param {object} params
	 * @returns {Indicator}
	 */
	addIndicator(id, params = { id }) {
		if (id && !params.id) params.id = id;

		const newIndicator = this.addIndicators([params]);

		return newIndicator.length > 0
			? newIndicator[0]
			: null;
	}

	/**
	 * Add multiple Indicator objects to the indicators collection
	 * @param {object[]} indicatorParams - An array of indicator params (panel reference added automatically)
	 * @returns {Indicator[]}
	 */
	addIndicators(indicatorParams) {
		let indicator;
		let IndicatorClass;
		const newIndicators = [];
		for (const [index, params] of indicatorParams.entries()) {
			IndicatorClass = Panel.getIndicatorClassName(params.id);
			if (IndicatorClass) {
				// Is this a lower indicator being placed on the upper panel?
				const foundLioup =
					this.isUpper &&
					this._checkForLioup(
						params.id,
						index + this.indicators.length
					);
				if (foundLioup) params.lowerIndicatorOnUpperPanel = true;

				// Build args for indicator constructor
				const args = {
					panel: this,
					params
				};

				if (params.lowerIndicatorOnUpperPanel) {
					// Add new scale to yAxis
					const newScaleUid = this.yAxis.addScale();

					// Change scale reference if lower indicator is on upper panel (point to the new scale)
					args.scaleUids = [this.xAxis.primaryScaleUid, newScaleUid];
				}

				// Create new indicator
				indicator = new IndicatorClass(args);

				// Add new indicator to panel indicator array
				this.indicators.push(indicator);
				newIndicators.push(indicator);

				// Comparison was added
				if (
					indicator.params.id === 'price' &&
					indicator.params.symbol !== this.core.params.symbol
				) {
					this.core.eventEmitter.emit(eventTypes.COMPARISON_ADD, { issueId: indicator.params.symbol });
					// Reset the domain since the new request can have different exchangeDates and will make the old domain invalid (CH-1504)
					this.core.resetDomain();
				}

				// Emit new indicator
				const event = this.isUpper
					? eventTypes.INDICATOR_UPPER_ADD
					: eventTypes.INDICATOR_LOWER_ADD;
				this.core.eventEmitter.emit(event, {
					indicator,
					indicatorType: indicator.params.id
				});
			}
		}

		// Create any needed component series flags
		this.resetComponentSeriesFlags();

		return newIndicators;
	}

	/** create or remove component series flags for this panel's indicators, depending on the param boolean */
	resetComponentSeriesFlags() {
		this.indicators.forEach(indicator => {
			// only run for indicators that have a componentSeries param
			if (indicator.params.componentSeries) {
				const { componentSeries } = indicator.params;

				if (this.core.params.showComponentSeriesFlags) {
					// create indicator flags
					for (const c of componentSeries) {
						const { id } = c;

						if (!indicator.flags[id]) {
							indicator.flags[id] = new IndicatorFlag({
								id: 'indicator',
								indicator,
								panel: this,
								seriesId: id
							});
						}
					}
				} else {
					// remove indicator flags
					for (const c of componentSeries) {
						const { id } = c;

						if (indicator.flags[id]) {
							indicator.flags[id].remove();
							delete indicator.flags[id];
						}
					}
				}
			}
		});
	}

	/**
	 * Returns true if mouse coordinates are within chart region (not including padding)
	 * @param {Number} x - X mouse coordinate for modcharts-rootmouse
	 * @param {Number} y - Y mouse coordinate for modcharts-rootmouse
	 */
	isWithin([x, y]) {
		const { height, padding, top, width } = this.size;

		return (
			x > padding.left &&
			x < padding.left + width &&
			y > top + padding.top &&
			y < top + padding.top + height
		);
	}

	/**
	 * add a new Event object to the events collection
	 * @param {string} id
	 * @param {object} params
	 */
	addEvent(id, params) {
		const args = { params, panel: this };
		let event;

		switch (id) {
			case 'dividends':
				event = new DividendEvent(args);
				break;
			case 'dividendscomplete':
				event = new DividendCompleteEvent(args);
				break;
			case 'splits':
				event = new SplitsEvent(args);
				break;
			case 'earnings':
				event = new EarningsEvent(args);
				break;
			case 'dividendscustom':
				event = new DividendCustomEvent(args);
				break;
			case 'splitscustom':
				event = new SplitsCustomEvent(args);
				break;
			case 'earningscustom':
				event = new EarningsCustomEvent(args);
				break;
			case 'announcedearnings':
				event = new AnnouncedEarningsEvent(args);
				break;
			case 'custom':
				event = new CustomEvent(args);
				break;
			default:
				break;
		}

		if (event) this.events.push(event);

		this.core.eventEmitter.emit(eventTypes.EVENT_ADD, {
			eventType: id
		});

		return event;
	}

	/**
	 * Set primary yAxis scaling method
	 * @param {string} scaleType - "linear" or "log"
	 */
	setScale(scaleType) {
		// don't allow log scales on normalized charts (has problems with zeroes and negative numbers)
		if (scaleType === 'log' && this.isNormalized) scaleType = 'linear';

		if (this.index > 0) scaleType = 'linear';

		this.core.params.yAxisScale = scaleType;

		this.yAxis.primaryScale = this.yAxis.createScale(
			this.yAxis.primaryScale.domain(),
			scaleType
		);

		this.core.eventEmitter.emit(eventTypes.PANEL_SCALE_CHANGE, { scaleType });
	}

	/**
	 * Get the min and max values for the given indicator.  This is used to determine the current y-axis range.
	 * @param {*} indicator
	 * @param {*} data
	 * @return {number[]}
	 */
	_getDataRange(indicator, data) {
		let min = Infinity;
		let max = -Infinity;

		for (const indicatorType in data) {
			if (
				data[indicatorType][indicator.params.uid] &&
				typeof indicator.params.visible === 'boolean' && indicator.params.visible
			) {
				const dataSeries = {};
				dataSeries[indicatorType] = data[indicatorType][indicator.params.uid];

				const rangeMin = indicator.getRangeMin(dataSeries);
				const rangeMax = indicator.getRangeMax(dataSeries);

				if (typeof rangeMin === 'number' && !isNaN(rangeMin)) {
					min = Math.min(min, rangeMin);
				}
				if (typeof rangeMax === 'number' && !isNaN(rangeMax)) {
					max = Math.max(max, rangeMax);
				}

				// ensure the yaxis has a range when min === max
				if (min === max) {
					min = min * 0.95;
					max = max * 1.05;
				}
			}
		}

		return { min, max };
	}

	/**
	 * Apply any max/min adjustments to range
	 * @param {number} min
	 * @param {number} max
	 * @param {number} maxLineWidth
	 * @param {Object} makeAdjustment
	 */
	_adjustRange(min, max, maxLineWidth, adjustForMarker = { max: true, min: true }) {
		if (
			this.core.params.yAxisScale === 'log' &&
			this.isUpper
		) {
			min = Math.max(min, 0.00001); // ensure min doesn't contain 0
		} else {
			// vertical yaxis padding (used to carve out room for chart legends, etc)
			const yAxisPadding = this._getYAxisPadding();

			// get number of pixels per price unit
			const pxDist = this.size.height / (max - min);

			// adjust max value by the specific price value to create a gap of specified pixel height
			max += yAxisPadding.top / pxDist;

			// adjust min value by the specific price value to create a gap of specified pixel height
			min -= yAxisPadding.bottom / pxDist;

			// Adjust max/min for the width of marker lines themselves
			if (adjustForMarker.max && yAxisPadding.top < maxLineWidth) max += maxLineWidth / pxDist;
			if (adjustForMarker.min && yAxisPadding.bottom < maxLineWidth) min -= maxLineWidth / pxDist;
		}

		return { min, max };
	}

	/**
	 * Checks to see if given marker type is part of the draw to bottom markers list
	 * @param {string} markerType - Marker type to check
	 * @return {Boolean} Whether or not marker type is on the list
	 */
	_checkForDrawToBottomMarker(markerType) {
		const drawToBottomMarkers = ['bar', 'posneg'];
		return !!drawToBottomMarkers.find(type => type === markerType);
	}

	/**
	 * Checks to see if given ID is part of the lower indicators on upper panel list
	 * @param {string} id - Indicator ID (eg. volume) to check
	 * @return {Boolean} Whether or not ID is on the list
	 */
	_checkForLioup(id, index) {
		const lowerIndicatorsOnUpperPanel = [
			'volume',
			'nrv'
		];
		return index > 0 && !!lowerIndicatorsOnUpperPanel.find(lioup => lioup === id);
	}

	/**
	 * Returns the max line width value given a current max value and and indicator with params
	 * @param {number} currentMax - The current max line width
	 * @param {Indicator} indicator - The indicator to check for a line width param
	 * @return {number} The updated max line width
	 */
	_updateMaxLineWidth(currentMax, indicator) {
		if (
			indicator.params.style &&
			typeof indicator.params.style.width === 'number'
		) {
			currentMax = Math.max(
				currentMax,
				indicator.params.style.width
			);
		}

		return currentMax;
	}

	/**
	 * Returns the min/max data range for all indicator datasets, based on the current slice of data
	 * Also adjusts for the Y-axis padding settings and marker thickness.
	 * @param {object[]} data
	 * @return {object}
	 */
	_getOverallDataRange(data) {
		// min/max calcs
		let min = Infinity;
		let max = -Infinity;
		let dataSymbol;
		let maxLineWidth = 1;
		const adjustForMarker = { max: true, min: true };
		const primarySymbol = this.core.params.symbol;

		// get min/max of each indicator's current range
		for (const [index, indicator] of this.indicators.entries()) {
			// Skip LIOUP's in this calculation
			const foundLioup = this.isUpper && this._checkForLioup(indicator.params.id, index);
			if (foundLioup) continue;

			maxLineWidth = this._updateMaxLineWidth(maxLineWidth, indicator);

			dataSymbol = indicator.params.symbol || primarySymbol;

			// Update min/max for data associated with current indicator
			const rangeBounds = this._getDataRange(indicator, data[dataSymbol]);
			if (rangeBounds.min < min) min = rangeBounds.min;
			if (rangeBounds.max > max) max = rangeBounds.max;

			// Don't adjust max value for marker thickness if marker type draws to bottom of chart
			const { markerType } = indicator.params;
			const foundDrawToBottomMarker = this._checkForDrawToBottomMarker(markerType);
			if (foundDrawToBottomMarker) adjustForMarker.min = false;
		}

		// adjust for user-defined range values
		this.params.yAxis.rangeValues.forEach(val => {
			min = Math.min(min, val);
			max = Math.max(max, val);
		});

		// Make adjustments for Y-axis padding and marker thickness
		({ min, max } = this._adjustRange(
			min,
			max,
			maxLineWidth,
			adjustForMarker
		));

		// ensure 0.0 is always visible on normalized charts
		if (this.isNormalized && !this.core.params.endOffsetDays) {
			min = Math.min(min, 0);
			max = Math.max(max, 0);
		}

		return { min, max };
	}

	/** inspect panel and indicator params for current padding value. */
	_getYAxisPadding() {
		// panel level
		let top = this.params.style.yAxis.padding.top || 0;
		let bottom = this.params.style.yAxis.padding.bottom || 0;

		// Certain indicators can override the value (currently those that contain bands, but not those using a secondary scale)
		this.indicators.forEach(indicator => {
			// Make sure padding styles exist and that indicator is using primary scale
			if (
				indicator.params.style.yAxisPadding &&
				indicator.scaleUids[1] === this.yAxis.primaryScaleUid
			) {
				if (typeof indicator.params.style.yAxisPadding.top === 'number') {
					top = indicator.params.style.yAxisPadding.top;
				}

				if (typeof indicator.params.style.yAxisPadding.bottom === 'number') {
					bottom = indicator.params.style.yAxisPadding.bottom;
				}
			}
		});

		return { top, bottom };
	}

	/**
	 * similar to _getOverallDataRange, this determines the min/max for all custom datasets based on the current slice
	 * of data about to be rendered.
	 */
	_getCustomDataRange(data) {
		// min/max calcs
		let min = Infinity;
		let max = -Infinity;
		let rangeMin;
		let rangeMax;
		let maxLineWidth = 0;

		for (const [index, el] of this.indicators.entries()) {
			// Skip volume indicator in this calculation
			const foundLioup = this.isUpper && this._checkForLioup(el.params.id, index);
			if (foundLioup) continue;

			// don't include hidden indicators
			if (typeof el.params.visible === 'boolean' && !el.params.visible) continue;

			if (el.params.style && el.params.style.width) {
				maxLineWidth = Math.max(
					maxLineWidth,
					el.params.style.width
				);
			}

			rangeMin = el.getRangeMin(data[el.params.datasetId]);
			rangeMax = el.getRangeMax(data[el.params.datasetId]);

			min = Math.min(rangeMin, min);
			max = Math.max(rangeMax, max);
		}

		/**
		 * vertical padding (used to carve out room for chart legends, etc)
		 */

		// get number of pixels per price unit
		const pxDist = this.size.height / (max - min);

		if (this.params.style.yAxis.padding.top) {
			// adjust max value by the specific price value to create a gap of specified pixel height
			max += this.params.style.yAxis.padding.top / pxDist;
		}

		if (this.params.style.yAxis.padding.bottom && min !== 0) {
			// adjust min value by the specific price value to create a gap of specified pixel height
			min -= this.params.style.yAxis.padding.bottom / pxDist;
		}

		// adjust max/min for the width of marker lines themselves
		max += maxLineWidth / pxDist;
		min -= maxLineWidth / pxDist;

		return { min, max };
	}

	/** search panel for indicator id ("sma", "bollinger") */
	getIndicatorsById(id) {
		const indicators = [];

		for (let x = 0; x < this.indicators.length; x++) {
			if (id === this.indicators[x].params.id) {
				indicators.push(this.indicators[x]);
			}
		}

		return indicators;
	}

	/** Measure the preceding panels' heights to get current top position */
	get calculatedTop() {
		let top = 0;

		for (const panel of this.core.panels) {
			const { size } = panel;

			// Add top margin before check because we should always add the top margin
			top += size.margin.top;

			// Stop adding if we reached this panel
			if (panel === this) break;

			top +=
				size.height +
				// size.margin.top +
				size.padding.bottom +
				size.padding.top +
				panel.getPanelMarginBottom(); // This shouldn't be necessary here, but using it for consistency
		}

		return top;
	}

	/**
 * Calculates the height of a chart panel. This function determines the panel height based on various parameters,
 * considering specific panel size settings, ratios, and the overall chart dimensions. It handles different scenarios,
 * such as when height values are explicitly set or need to be computed based on other factors.
 *
 * The function follows a set priority to determine the panel height:
 * 1. Direct panel size parameters.
 * 2. Panel size ratios (relative to the chart width).
 * 3. Global chart panel size parameters.
 * 4. Global chart panel size ratios.
 *
 * The calculated height includes padding and margin, and is adjusted based on the percentage of total chart height,
 * if not manually set.
 */
	get calculatedHeight() {
		const { isUpper } = this;
		let value = 0;
		let doAutosize = false;

		// Direct panel size setting has the highest priority
		if (this.params.size.height) {
			value = this.params.size.height;
		}
		// If panel height isn't set, use core parameters for upper or lower panels
		else if (this.core.params.panelHeightUpper && isUpper) {
			value = this.core.params.panelHeightUpper;
		} else if (this.core.params.panelHeightLower && !isUpper) {
			value = this.core.params.panelHeightLower;
		}
		// When panel sizes are not set, use panel to chart width ratios to compute height
		else if (this.core.params.usePanelComputedHeight) {
			value = this.core.size.width; // Starting point for ratio calculation

			// Apply the appropriate height to width ratio
			if (this.params.heightToChartWidthRatio) {
				value *= this.params.heightToChartWidthRatio;
			} else if (this.core.params.panelHeightToChartWidthRatioUpper && isUpper) {
				value *= this.core.params.panelHeightToChartWidthRatioUpper;
			} else if (this.core.params.panelHeightToChartWidthRatioLower && !isUpper) {
				value *= this.core.params.panelHeightToChartWidthRatioLower;
			} else {
				// Warn if no applicable ratio is found
				this.core.warn('No panel-height-to-chart-width ratio found. Autosizing panel.');
				doAutosize = true;
			}
		} else {
			// If no height or ratio is set, enable autosizing
			doAutosize = true;
		}

		// Autosize the panel height based on chart height and panel proportions
		if (doAutosize) {
			let nonChartHeight = 0;

			// Calculate the total non-chart height (padding and margin)
			for (const panel of this.core.panels) {
				const { size } = panel;
				nonChartHeight += size.padding.bottom + size.padding.top +
							panel.getPanelMarginBottom() + panel.getPanelMarginTop();
			}

			// Calculate the panel height as a proportion of the remaining chart height
			value = Math.round(
				this.size.heightPct * (this.core.size.height - nonChartHeight)
			);
		}
		return Math.max(0, value);
	}


	/**
	 * Returns the bottom margin for the parent panel based upon panel order and param flag
	 * @returns {Number} Value (in pixels) to be used for the panel bottom margin
	 */
	getPanelMarginBottom() {
		// Don't use the last panel bottom margin if flag is set
		return this === this.core.panels.slice(-1)[0] &&
			this.core.params.ignoreLastPanelBottomMargin
			? 0
			: this.size.margin.bottom;
	}

	/**
	 * Returns the top margin for the parent panel based upon panel order and param flag
	 * @returns {Number} Value (in pixels) to be used for the panel top margin
	 */
	getPanelMarginTop() {
		return this.isUpper ||	!this.core.params.ignoreLowerPanelTopMargin
			? this.size.margin.top
			: 0;
	}

	/** Initialize the panel size object */
	initPanelSize() {
		this.size = {
			height: 0,
			heightPct: this.params.size.heightPct,
			heightToChartWidthRatio: this.params.size.heightToChartWidthRatio,
			padding: { ...this.params.style.padding },
			margin: { ...this.params.style.margin },
			top: 0,
			width:
				+this.core.size.width -
				this.params.style.padding.left -
				this.params.style.padding.right
		};
	}

	/**
	 * Calculates the maximum text width for a given array of labels.
	 * @param {string[]} labels - An array of label strings to measure.
	 * @param {CanvasRenderingContext2D} ctx - The canvas rendering context used for measuring text width.
	 * @returns {number} The width of the longest label in pixels.
	 */
	_getMaxTextWidth(labels, ctx) {
		const { style } = this.params;
		if (labels.length === 0) return 0;

		// Sort the array based on the length of the strings in descending order
		labels.sort((a, b) => b.length - a.length);

		// Measure the width of the longest string
		const longestText = labels[0];
		ctx.textAlign = style.yAxis.label.textAlign;
		ctx.textBaseline = style.yAxis.label.textBaseline;
		ctx.fillStyle = style.yAxis.label.color;
		ctx.font = this.getFontStyle(
			style.yAxis.font.weight,
			style.yAxis.font.size,
			style.yAxis.font.family,
			style.yAxis.font.style
		);
		const textWidth = ctx.measureText(longestText).width;
		return textWidth;
	}

	/**
	 * Return the width of the YAxis padding. Normally this would just be a style, but now it can also support an auto width mode.
	 * @param {string} side - The side of the padding to calculate ('left' or 'right').
	 * @returns {number} The padding value in pixels for the specified side.
	 */
	_getPadding(side) {
		let padding = this.params.style.padding[side];

		if (this.core.params.panelYAxisAutoWidth && this.core.params.yAxisSide === side) {
			if (this.isUpper) {
				let labels = [];
				this.core.panels.forEach(panel => {
					const labelObjs = panel.yAxis.calculateRenderedLabels();
					const newLabels = labelObjs.map(row => row.label);
					labels = labels.concat(newLabels);
				});

				// Find the widest string using the YAxis font styles
				const maxWidth = this._getMaxTextWidth(labels, this.rootContext);
				padding = maxWidth + this.params.style.yAxis.label.padding.left + this.params.style.yAxis.label.padding.right;

				// Search for the flag with id 'lastClose' within the flags array
				const lastCloseFlag = this.indicators.length && this.indicators[0].flags.lastClose ? this.indicators[0].flags.lastClose : null;

				// Check if the lastCloseFlag exists and then calculate its width
				if (lastCloseFlag) {
					const lastCloseWidth = lastCloseFlag._calculateFlagLabelWidth();
					padding = Math.max(padding, lastCloseWidth);
				}

			} else if (this.core.panels?.length) {
				// If this is a lower panel, reuse the first panel's calculated padding so we don't have to recalculate
				padding = this.core.panels[0].size.padding[side];
			}
		}

		return padding;
	}

	_getPaddingRight() {
		return this._getPadding('right');
	}

	_getPaddingLeft() {
		return this._getPadding('left');
	}

	/**
	 * This method updates dynamic size properties of the panel:
	 * - Resizes and repositions panel DOM elements - the panel DIV itself, its CANVAS element, the error DIV, and the panel legend DIV
	 * - Updates the panel.size collection
	 */
	resize() {
		const { calculatedTop, core } = this;
		const rootOverlay = select(this.rootOverlay.node().parentNode);

		// Update size collection
		this.size.height = this.calculatedHeight; // dynamic height
		this.size.top = calculatedTop; // this panel's top, relative to the top of the overall chart container
		this.size.padding.right = this._getPaddingRight(); // either defined in styles, or auto-calculated based on widest label
		this.size.padding.left = this._getPaddingLeft(); // either defined in styles, or auto-calculated based on widest label
		this.size.left = this.size.padding.left;
		this.size.width = core.size.width - this.size.padding.left - this.size.padding.right;

		// Calculate canvas size collection
		const canvasSize = {
			height: this.size.height + this.size.padding.bottom + this.size.padding.top,
			left: 0,
			top: this.size.top,
			width: core.size.width
		};

		// Update DOM elements
		const ratio = core.getScreenRatio();
		const items = [
			this.rootCanvas,
			rootOverlay,
			this.rootTools
		];

		this.rootPanel.style('top', `${canvasSize.top}px`);

		for (const el of items) {
			const canvas = el.node();

			if (!canvas) return;

			if (ratio !== 1 && el !== rootOverlay) {
				// scale canvas by device ratio for High DPI screens
				canvas.width = Math.round(canvasSize.width * ratio);
				canvas.height = Math.round(canvasSize.height * ratio);

				canvas.style.width = `${canvasSize.width}px`;
				canvas.style.height = `${canvasSize.height}px`;

				canvas.getContext('2d').scale(ratio, ratio);

			} else if (canvasSize.height >= 0) {
				if (el !== rootOverlay) {
					canvas.width = canvasSize.width;
					canvas.height = canvasSize.height;
				}

				canvas.style.width = `${canvasSize.width}px`;
				canvas.style.height = `${canvasSize.height}px`;
			}
		}

		// Error container
		this.rootError
			.style('width', `${this.size.width}px`)
			.style('height', `${this.size.height}px`)
			.style('line-height', `${this.size.height}px`)
			.style('text-align', 'center');

		// Legend container
		this.rootLegend
			.style('width', `${this.size.width + this.size.padding.left}px`)
			.style('padding-left', `${this.size.padding.left}px`);
	}

	/** return true if panel is using normalized data */
	get isNormalized() {
		const { isUpper } = this;

		if (this.core.params.dataNormalized && isUpper) return true;

		if (this.core.comparisons.length && isUpper) return true;

		if (this.core.params.symbolCompare.length && isUpper) return true; // backward compatibility (CH-1504)

		if (this.getIndicatorsById('sectorindustry').length) return true;

		return false;
	}

	/** return true if panel is first on chart */
	get isUpper() {
		return this === this.core.panels[0];
	}

	/**
	 * Create a clip covering the chart area of the panel. This should be called BEFORE
	 * drawing on the canvas to properly clip the canvas.
	 * @param {CanvasRenderingContext2D} ctx - Canvas rendering context on which to create a clip
	 * @param {Number} clipWidth - Multipler used to increase the clip width. Defaults to 1.
	 */
	clipPanel(ctx, clipWidth = 1) {
		ctx = ctx || this.rootContext;

		const top = this.size.padding.top;
		const left = this.size.padding.left;
		const width = this.size.width * clipWidth;
		const height = this.size.height;

		ctx.beginPath();
		ctx.rect(left, top, width, height);
		ctx.clip();
	}

	/**
	 * render all axes, indicators and tools for this panel
	 * clip contents to panel region to remove any overlaps
	 */
	render(data, dateLabels, clipWidth) {
		if (!data && this.prevData) data = this.prevData;
		if (!dateLabels && this.prevDateLabels) dateLabels = this.prevDateLabels;

		this.prevData = data;
		this.prevDateLabels = dateLabels;

		// calculate event positions before rendering
		this.getEventPositions(data[this.core.params.symbol]);

		// legend
		this.revealLegend();

		// render axes
		this.renderAxes(dateLabels);

		// prepare main clip
		this.rootContext.save();

		// clip region around panel
		this.clipPanel(this.rootContext, clipWidth);

		// render indicators
		this.renderIndicators(data);

		// render extendedHours marker
		this.renderExtendedHours();

		// render special sessions
		this.renderSessions('SessionBreak');
		this.renderSessions('PreMarketTrading');
		this.renderSessions('PostMarketTrading');

		// restore clipped panel
		this.rootContext.restore();

		// render event icons outside of clip
		this.renderEvents(data);

		// render no-clip indicators outside of clip
		this.renderNoClipIndicators(data);

		// render tools
		this.renderTools();

		// render borders
		this.renderBorders();

		// render indicator flags outside of clip
		this.renderFlags();
	}

	/** Render the majority of panel indicators to the primary canvas element (skip no-clip indicators) */
	renderIndicators(data) {
		let indicatorSymbol = '';
		let elType = '';
		let ds = {};
		let exchangeId = '';
		const primarySymbol = this.core.params.symbol;
		const isCustom = this.indicators.length && this.indicators[0].params.id === 'custom';

		this.sessionClips = {};

		for (const indicator of this.indicators) {
			// Skip non-visible and no-clip indicators
			if (!indicator.params.visible || indicator.params.noClip) continue;

			// Custom indicators using custom datasets
			if (
				indicator.params.id === 'custom' &&
				data[indicator.params.datasetId]
			) {
				indicator.render(data[indicator.params.datasetId]);

			} else if (indicator.params.id === 'bestworst') {

				if (isCustom) {
					ds['price'] = data[this.indicators[0].params.datasetId].map(row => {
						return {
							date: row.date,
							close: row.value
						};
					});
				} else if (data[primarySymbol] && data[primarySymbol].price) {
					ds['price'] = this.core.getFirstDataset(
						data[primarySymbol].price
					);
				}
				indicator.render(ds);
			}

			indicatorSymbol = indicator.params.symbol || primarySymbol;

			// API-based indicators (data.MSFT.price, data.MSFT.sma, ...)
			for (elType in data[indicatorSymbol]) {
				if (Object.prototype.hasOwnProperty.call(data[indicatorSymbol], elType)) {
					// data.MSFT.price.<uid>
					if (data[indicatorSymbol][elType][indicator.params.uid]) {
						// construct the dataset passed to this indicator's render method
						ds = {};
						ds[elType] =
							data[indicatorSymbol][elType][indicator.params.uid];

						// special case: the "posneg" price indicator needs an additional previousclose datapoint in order to render
						if (
							data[indicatorSymbol].previousclose &&
							elType === 'price' &&
							indicator.params.markerType === 'posneg'
						) {
							ds['previousclose'] = this.core.getFirstDataset(
								data[indicatorSymbol].previousclose
							);
						}

						// special case: the "volume" and "nrv" indicators will need the price dataset when in "posneg" mode.
						if (
							data[indicatorSymbol].price &&
							(elType === 'volume' || elType === 'nrv') &&
							indicator.params.markerType === 'posneg'
						) {
							ds['price'] = this.core.getFirstDataset(
								data[indicatorSymbol].price
							);
						}

						// if intraday we need to clip to individual exchange sessions
						if (this.core.state.isIntraday) {

							if (this.core.xref[indicatorSymbol]) {
								exchangeId = this.core.xref[indicatorSymbol].exchangeId;

								// clip rectangular regions around intraday sessions
								if (this.core.params.clipSessions) {
									// normal session
									this.renderSession(
										ds,
										exchangeId,
										indicator,
										'NormalSession'
									);

									// extended sessions
									if (this.core.params.extendedHours) {
										this.renderSession(
											ds,
											exchangeId,
											indicator,
											'PreMarketTrading'
										);
										this.renderSession(
											ds,
											exchangeId,
											indicator,
											'PostMarketTrading'
										);
									}

									// session breaks (eg. lunch breaks). for performance, only render if session breaks actually exist
									if (
										this.core.timeService.sessionBreak &&
										this.params.style.sessionBreak
									) {
										this.renderSession(
											ds,
											exchangeId,
											indicator,
											'SessionBreak'
										);
									}

								} else {
									// don't clip sessions
									indicator.render(ds);
								}
							} else {
								indicator.render(ds);
							}
						} else {
							// not intraday; render everything
							indicator.render(ds);
						}
					}
				}
			}
		}
	}

	/**
	 * render a single intraday indicator clipped to its exchange sessions.  this eliminates any line segments between sessions.
	 */
	renderSession(ds, exchangeId, indicator, sessionType) {
		this.rootContext.save();

		if (!this.sessionClips[exchangeId]) {
			this.sessionClips[exchangeId] = {};
		}

		if (!this.sessionClips[exchangeId][sessionType]) {
			this.sessionClips[exchangeId][sessionType] = this.getSessionClips(
				exchangeId,
				sessionType
			);
		}

		this.clipIntradaySessions(this.sessionClips[exchangeId][sessionType]);

		indicator.render(ds, sessionType);

		this.rootContext.restore();
	}

	/** Render event icons outside of clip */
	renderEvents(data) {
		for (const event of this.events) {
			if (event.params.visible) event.render(data[this.core.params.symbol] || {});
		}
	}

	/** Render intraday extended hours vertical bands */
	renderExtendedHours() {
		if (!this.core.state.isIntraday) return;

		if (!this.extendedHoursMarker) {
			this.extendedHoursMarker = new ExtendedHoursMarker({}, this);
		}

		const primaryExchange = this.core.xref[this.core.params.symbol]
			? this.core.xref[this.core.params.symbol].exchangeId
			: null;

		if (this.sessionClips[primaryExchange]) {
			this.extendedHoursMarker.render(this.sessionClips[primaryExchange]);
		}
	}

	/** Calculate the coords property for all event icons */
	getEventPositions(data) {
		for (let i = 0; i < this.events.length; i++) {
			this.events[i].coords = null;
		}

		for (let i = 0; i < this.events.length; i++) {
			if (this.events[i].params.id === 'custom') {
				this.events[i].coords = this.events[i].getPositions(
					{
						custom: [this.events[i].params.dataset],
						price: data.price
					},
					'custom'
				);
			} else {
				this.events[i].coords = this.events[i].getPositions(
					data,
					this.events[i].params.id
				);
			}
		}
	}

	/** Render special indicators that aren't clipped to their intraday sessions (eg highlow, volumebyprice, etc) */
	renderNoClipIndicators(data) {
		let indicatorSymbol = '';
		let indicator = {};
		let elType = '';
		let ds = {};
		const primarySymbol = this.core.params.symbol;

		for (let i = 0; i < this.indicators.length; i++) {
			indicator = this.indicators[i];

			// Only draw visble, no-clip indicators
			if (!indicator.params.visible || !indicator.params.noClip) continue;

			indicatorSymbol = indicator.params.symbol || primarySymbol;
			ds = {};

			// Non-API driven indicators
			switch (indicator.params.id) {
				case 'highlow':
					// the highlow indicator uses the price dataset
					if (data[primarySymbol] && data[primarySymbol].price) {
						ds['price'] = this.core.getFirstDataset(
							data[primarySymbol].price
						);
					}

					// special case:  the "highlow" indicator can use the previousclose dataset to avoid collisions.
					if (data[primarySymbol].previousclose) {
						ds['previousclose'] = this.core.getFirstDataset(
							data[primarySymbol].previousclose
						);
					}

					indicator.render(ds);

					continue;
				case 'horizontalannotation':
					indicator.render();

					continue;
				case 'linearregression':
					// The "linearregression" indicator uses the price dataset
					if (indicator.params.parentUid || indicator.params.parentUID) {
						const parentIndicator = this.core.getIndicatorByUid(
							indicator.params.parentUid || indicator.params.parentUID
						);

						const symbol = parentIndicator.params.symbol || indicatorSymbol;
						ds['linearregression'] = this.core.getFirstDataset(
							data[symbol][parentIndicator.params.id]
						);
					} else if (data[primarySymbol] && data[primarySymbol].price) {
						ds['linearregression'] = this.core.getFirstDataset(
							data[primarySymbol].price
						);
					}
					indicator.render(ds);

					continue;
				case 'gsrecommendation':
					if (data[primarySymbol].gsrecommendation) {
						ds['gsrecommendation'] = data[primarySymbol].gsrecommendation[indicator.params.uid];
						indicator.render(ds);
					}
					continue;
				case 'volumebyprice':
					// the volumebyprice indicator uses the price dataset and volume dataset
					if (data[primarySymbol] && data[primarySymbol].price) {
						ds['price'] = this.core.getFirstDataset(
							data[primarySymbol].price
						);
					}

					if (data[primarySymbol] && data[primarySymbol].volume) {
						ds['volume'] = this.core.getFirstDataset(
							data[primarySymbol].volume
						);
					}

					indicator.render(ds);

					continue;
			}

			// API-based indicators (data.MSFT.price, data.MSFT.sma, ...)
			for (elType in data[indicatorSymbol]) {
				if (Object.prototype.hasOwnProperty.call(data[indicatorSymbol], elType)) {
					// data.MSFT.price.<uid>
					if (data[indicatorSymbol][elType][indicator.params.uid]) {
						// construct the dataset passed to this indicator's render method
						ds = {};
						ds[elType] =
							data[indicatorSymbol][elType][indicator.params.uid];

						indicator.render(ds);
					}
				}
			}
		}
	}

	/**
	 * return an array of rectangles representing clip regions for each exchange session.
	 * also draw holiday labels as needed
	 */
	getSessionClips(exchangeId, sessionType) {
		const clips = [];
		const dates = this.core.exchangeDates;
		const dateStart = new Date(dates[0]);
		const dateStop = new Date(dates[dates.length - 1]);
		const padding = this.size.padding;
		const scale = this.xAxis.primaryScale;
		const typicalSessions = this.core.timeService.typicalSessions;
		const debugSessions = false; // useful for visualizing clip regions during development
		const labelHeight = 10;
		const alternateSessions = this.core.timeService.alternateSessions;

		let sessions = [];
		let session = {};
		let left = 0;
		let right = 0;
		let mid = 0;
		let s = 0;
		let labelWidth = 0;
		let ctx = {};
		let dateLeft = new Date();
		let dateRight = new Date();
		let exchangeClosure = null;
		let regionWidthAdjustment = null;

		// default sessiontype
		if (!sessionType) {
			sessionType = 'NormalSession';
		}

		// ensure candlesticks don't get cut in half when a normal session is followed by SessionBreak or AlternateSession (CW-880)
		if (
			sessionType === 'NormalSession' &&
			Object.keys(this.core.xAxisDomainAdjustmentCategories).length &&
			this.core.panels[0].indicators[0].markers['candlestick']
		) {
			// extend clip region by this amount (half of one bar)
			regionWidthAdjustment =
				Math.ceil(
					this.core.panels[0].indicators[0].markers[
						'candlestick'
					].getBarWidth(this.core.panels[0]) / 2
				) + 1;
		}

		// start at midnight
		dateStart.setHours(0);
		dateStart.setMinutes(0);
		dateStart.setSeconds(0);
		dateStart.setMilliseconds(0);

		while (dateStart < dateStop) {
			// grab this day's sessions
			sessions = typicalSessions[dateStart.getDay()].sessions;

			// replace with alternatesessions if applicable (NXC-576)
			Object.keys(alternateSessions).forEach(key => {
				if (new Date(key) - dateStart === 0) {
					// this day's date matches alternatesessions date

					// grab this day's alternate sessions for this exchange
					const altSessions = alternateSessions[key].filter(row => row['exchangeId'] === exchangeId);

					// replace this day's sessions with alternate sessions
					if (altSessions.length > 0) sessions = altSessions;
				}
			});

			// exchange closed on this day?
			exchangeClosure = this.getExchangeClosure(dateStart, exchangeId);

			if (!exchangeClosure) {
				for (s = 0; s < sessions.length; s++) {
					session = sessions[s];

					if (
						session.exchangeId === exchangeId &&
						(!session.sessionType ||
							session.sessionType === sessionType)
					) {
						// left edge of current session
						dateLeft = new Date(
							dateStart.getFullYear(),
							dateStart.getMonth(),
							dateStart.getDate(),
							0,
							session.open
						);

						// right edge of current session
						dateRight = new Date(
							dateStart.getFullYear(),
							dateStart.getMonth(),
							dateStart.getDate(),
							0,
							session.close
						);

						left = scale(this.core.closestExchangeIndex(dateLeft));
						right = scale(
							this.core.closestExchangeIndex(dateRight)
						);

						// extend the region by half a bar width
						if (typeof regionWidthAdjustment === 'number') {
							right += regionWidthAdjustment;
						}

						clips.push({
							left,
							top: padding.top,
							width: Math.ceil(right - left),
							height: this.size.height + padding.top,
							debugColor: 'rgba(255, 0, 50, 0.1)',
							exchangeId: session.exchangeId
						});
					}
				}
			}

			// draw holiday closure labels
			if (
				exchangeClosure &&
				this.core.params.showClosureLabels &&
				this.isUpper &&
				(
					this.core.params.symbolCompare.length ||
					this.core.comparisons.length
				)
			) {
				const typicalSession = this.core.timeService.typicalSessions[
					dateStart.getDay()
				];

				if (typicalSession.rulerSessions[exchangeId]) {
					const rulerSession = typicalSession.rulerSessions[exchangeId];

					// left edge
					dateLeft = new Date(
						dateStart.getFullYear(),
						dateStart.getMonth(),
						dateStart.getDate(),
						0,
						rulerSession.open
					);

					// right edge
					dateRight = new Date(
						dateStart.getFullYear(),
						dateStart.getMonth(),
						dateStart.getDate(),
						0,
						rulerSession.close
					);

					ctx = this.rootContext;

					labelWidth = ctx.measureText(exchangeClosure.name).width;

					left = scale(this.core.closestExchangeIndex(dateLeft));
					right = scale(this.core.closestExchangeIndex(dateRight));
					mid = left + (right - left) / 2 - labelHeight / 2;

					// draw label if it fits
					if (
						this.size.height > labelWidth &&
						right - left > labelHeight
					) {
						ctx.save();

						ctx.translate(
							subpixel.px(mid),
							this.size.height / 2 + labelWidth / 2
						);
						ctx.rotate(-Math.PI / 2);
						ctx.fillStyle = this.params.style.label.closure.color || '#777';
						ctx.fillText(exchangeClosure.name, 0, 0);

						ctx.restore();
					}
				}
			}

			// increment by 1 day
			dateStart.setDate(dateStart.getDate() + 1);
		}

		// render optional debug information
		if (debugSessions) {
			this.rootContext.strokeWidth = 1;

			for (let x = 0; x < clips.length; x++) {
				if (clips[x].left + clips[x].width > 0) {
					this.rootContext.fillStyle =
						x % 2 === 0
							? clips[x].debugColor
							: 'rgba(255,255,50,0.1)';
					this.rootContext.fillRect(
						clips[x].left,
						clips[x].top,
						clips[x].width,
						clips[x].height
					);

					if (clips[x].width > 20) {
						this.rootContext.fillStyle = '#FFFFFF';
						this.rootContext.fillText(
							`${clips[x].exchangeId} ${sessionType}`,
							clips[x].left + 5,
							clips[x].height - 9
						);
					}
				}
			}
		}

		return clips;
	}

	/** Perform the canvas clip using generated clip regions */
	clipIntradaySessions(sessionClips) {
		const ctx = this.rootContext;
		let clip = {};

		// create one big clip region with several rectangles, one per session
		ctx.beginPath();

		for (let r = 0; r < sessionClips.length; r++) {
			clip = sessionClips[r];

			if (clip.left + clip.width > 0) {
				ctx.moveTo(clip.left, clip.top); //nw
				ctx.lineTo(clip.left, clip.height); //sw
				ctx.lineTo(clip.left + clip.width, clip.height); //se
				ctx.lineTo(clip.left + clip.width, clip.top); //ne
			}
		}

		ctx.clip();
		ctx.closePath();
	}

	/**
	 * return true if there is a closure for this exchange and date
	 */
	getExchangeClosure(date, exchangeId) {
		const closures = this.core.timeService.fullClosures;

		for (let x = 0, xLen = closures.length; x < xLen; x++) {
			if (date - closures[x].date === 0) {
				for (
					let i = 0, iLen = closures[x].exchangeIds.length;
					i < iLen;
					i++
				) {
					if (closures[x].exchangeIds[i] === exchangeId) {
						return closures[x];
					}
				}
			}
		}

		return false;
	}

	/**
	 * render tools clipped to chart boundaries
	 */
	renderTools() {
		// renderLowers
		// see if we have any upper tools that have a renderLower method (aka, OHLCCrosshair, returnsCalculator)
		const renderLowers = this.core.panels[0].tools.filter(tool => typeof tool.renderLower !== 'undefined');

		renderLowers.forEach(tool => {
			this.core.panels.forEach(panel => {
				if (!panel.isUpper) {
					panel.clearTools();
					panel.rootToolContext.save();
					panel.clipPanel(panel.rootToolContext);
					tool.renderLower(panel);
					panel.rootToolContext.restore();
				}
			});
		});

		// clip the panel so that no marker can overlap edges
		this.rootToolContext.save();
		this.clipPanel(this.rootToolContext);

		const flags = [];
		this.tools.forEach(tool => {
			if (tool.state.visible) {
				tool.render();
				if (tool.flags?.horizontalAnnotation) {
					const position = tool.flags.horizontalAnnotation.render();
					if (position?.y) {
						position.flag = tool.flags.horizontalAnnotation;
						position.tool = tool;
						flags.push(position);
					}
				}
			}
		});

		this.rootToolContext.restore();

		// optional adjustment for horizontalAnnotation flag collisions
		if (this.core.params.flagOverlap === false && flags.length > 0) {
			this.preventFlagOverlap(flags);
		}
	}

	/**
	 * Render indicator flags.
	 * Hide flags if showFlags mode is false, or if lastCloseFlagOnlyPrimary condition is not met.
	 */
	renderFlags() {
		const core = this.core;
		const flags = [];

		// Render flags in their default positions (may have overlaps)
		for (const ind of this.indicators) {
			for (const key in ind.flags) {
				if (!Object.prototype.hasOwnProperty.call(ind.flags, key)) continue;
				const flag = ind.flags[key];

				// Check if flags should be shown
				if (core.params.showFlags) {
					// Check for lastCloseFlagOnlyPrimary condition
					// CH-4898 If the flag type is 'lastClose' and the conditions are not met (primary panel and primary indicator), hide the flag
					if (
						core.params.lastCloseFlagOnlyPrimary &&
						key === 'lastClose' &&
						!(this.isUpper === true && ind === this.indicators[0])
					) {
						flag.hide();
						continue; // Skip rendering this lastClose flag
					}

					// Render the flag if the conditions are met
					const position = flag.render();
					if (position?.y) {
						position.flag = flag;
						flags.push(position);
					}
				} else {
					// Hide all flags if showFlags mode is false
					flag.hide();
				}
			}
		}

		// Optional adjustment for flag collisions
		if (core.params.flagOverlap === false && flags.length > 0) {
			this.preventFlagOverlap(flags);
		}
	}


	/**
	 * Adjust the y position of each non-primary flag to ensure no overlaps.
	 */
	preventFlagOverlap(flags) {
		if (!flags.length) return;
		const height = this.params.style.yAxis.font.size + flags[0].flag._getPadding('top') + flags[0].flag._getPadding('bottom');
		const halfHeight = height / 2;

		// Helper function to find the index of the primary flag.  This is the flag considered the most "important".  Other flags will be spread away from this flag as needed.
		const findPrimaryFlagIndex = () => {
			const index = flags.findIndex(flag =>
				flag.tool === this.core.state.dragTool ||
					(flag.flag && flag.flag.indicator && flag.flag.indicator.params.symbol === this.core.params.symbol)
			);
			// If no flag matches the criteria, default to the middle index of the array
			return index !== -1 ? index : Math.floor(flags.length / 2);
		};

		// Sort flags by their y position
		flags.sort((a, b) => a.y - b.y);

		const primaryIndex = findPrimaryFlagIndex();
		this.adjustFlagPositions(flags, primaryIndex, height);
		this.repositionFlags(flags, halfHeight);
	}

	/**
	 * Adjust flag positions based on the primary flag to prevent overlaps.
	 */
	adjustFlagPositions(flags, primaryIndex, height) {
		let lastY = flags[primaryIndex].y;

		// Adjust positions for flags after the primary flag
		for (let i = primaryIndex + 1; i < flags.length; i++) {
			if (flags[i].y < lastY + height) {
				flags[i].y = lastY + height;
			}
			lastY = flags[i].y;
		}

		// Adjust positions for flags before the primary flag
		lastY = flags[primaryIndex].y;
		for (let i = primaryIndex - 1; i >= 0; i--) {
			if (flags[i].y > lastY - height) {
				flags[i].y = lastY - height;
			}
			lastY = flags[i].y;
		}
	}

	/**
	 * Reposition flag SVG elements and hide flags near panel edges.
	 */
	repositionFlags(flags, halfHeight) {
		const { top, bottom } = this.size;

		flags.forEach(flag => {
			const flagY = top + flag.y;
			if (flagY + halfHeight > bottom || flagY - halfHeight < top) {
				flag.flag.hide();
			} else {
				flag.flag._updateFlagBody(this, flag.x, flag.y);
				flag.flag._updateFlagLabel(this, flag.val, flag.x, flag.y);
			}
		});
	}


	/** Clear out the current context and tools */
	clearPanel() {
		const { style } = this.params;
		const { rootContext: ctx } = this;
		const { height, padding, width } = this.size;
		const fullWidth = width + padding.left + padding.right;
		const fullHeight = height + padding.top + padding.bottom;

		// empty canvas contents
		ctx.clearRect(0, 0, fullWidth, fullHeight);

		// fill grid area rect
		if (style.grid.background.color) {
			ctx.fillStyle = style.grid.background.color;
			ctx.fillRect(padding.left, padding.top, width, height);
		}

		// fill yaxis rect
		if (style.yAxis.background.color) {
			ctx.fillStyle = style.yAxis.background.color;
			ctx.fillRect(
				padding.left + width,
				padding.top,
				padding.right,
				height
			);
		}

		// fill xaxis rect
		if (style.xAxis.background.color) {
			ctx.fillStyle = style.xAxis.background.color;
			ctx.fillRect(
				padding.left,
				padding.top + height,
				fullWidth,
				padding.bottom
			);
		}

		// clear tools canvas
		this.clearTools();

		// clear legends
		this.concealLegend();

		// clear flags
		this.clearFlags();
	}

	/** Hide all panel and indicator flags */
	clearFlags() {
		// remove panel flags
		Object.keys(this.flags).forEach(key => {
			const flag = this.flags[key];
			flag.hide();
		});

		// remove indicator flags
		Object.keys(this.indicators).forEach(key => {
			const indicator = this.indicators[key];
			Object.keys(indicator.flags).forEach(indicatorFlagKey => {
				const flag = indicator.flags[indicatorFlagKey];
				flag.hide();
			});
		});

		// remove tool flags
		Object.keys(this.tools).forEach(key => {
			const tool = this.tools[key];
			Object.keys(tool.flags).forEach(toolFlagKey => {
				const flag = tool.flags[toolFlagKey];
				flag.hide();
			});
		});
	}

	/** Clears the tool canvas for this panel */
	clearTools() {
		const canvasWidth =
				this.size.width +
				this.size.padding.left +
				this.size.padding.right;
		const canvasHeight =
				this.size.height +
				this.size.padding.top +
				this.size.padding.bottom;

		this.rootToolContext.clearRect(0, 0, canvasWidth, canvasHeight);
	}

	/** Internal method for setting up axes for chart render */
	configureAxes() {
		let domain;
		const { customData } = this.core.params;
		const data = customData
			? this.core._getCustomFrameData()
			: this.core._getFrameData();

		if (
			this.params.yAxis.range &&
			this.params.yAxis.range.length === 2
		) {
			// user-specified yaxis domain
			domain = {
				min: this.params.yAxis.range[0],
				max: this.params.yAxis.range[1]
			};
		} else {
			// get min/max values for every indicator in filtered set
			domain = customData
				? this._getCustomDataRange(data)
				: this._getOverallDataRange(data);
		}

		const start = this.size.padding.left;
		const stop = start + this.size.width;

		// Update scale range
		this.xAxis.setScaleRange({ start, stop });

		// Update range for present xAxis scale (for calculating zoom transform updates)
		if (this.index === 0) this.core.state.xAxisPresentScale.range([start, stop]);

		if (this.core.zoom) {
			// Use the panel width here because rootmouse covers the entire panel
			const { height, padding, width } = this.size;

			// Set transform extent to only exclude the grid area (not the yAxis area)
			this.core.zoom.extent([[padding.left, padding.top], [width, height]]);
		}

		// Only apply .nice() if domain doesn't intersect 0
		const shouldSetNice = domain.max !== 0 && domain.min !== 0;

		// Assume no draw to bottom markers in use
		let foundDrawToBottomMarker = false;

		const range = {
			min: this.size.padding.top,
			max: this.size.padding.top + this.size.height
		};

		// Set domain for primary Y-axis scale
		this.yAxis.setScaleDomain({
			shouldSetNice,
			start: domain.max,
			stop: domain.min
		});

		for (const [index, i] of this.indicators.entries()) {
			const { markerType } = i.params;
			const foundLioup = this.isUpper && this._checkForLioup(i.params.id, index);

			if (!foundLioup) {
				// Check if indicator has marker that draws to bottom
				foundDrawToBottomMarker =
				foundDrawToBottomMarker ||
				this._checkForDrawToBottomMarker(markerType);
			} else {
				// Set domain/range for secondary Y-axis scales
				const secondaryDomain = this._getDataRange(
					i,
					// Should we get the symbol in a different way?
					data[this.core.params.symbol]
				);

				// Get % of panel height for LIOUP height (default to full panel height)
				let { panelHeightRatio = 1 } = i.params.style;
				if (
					panelHeightRatio > 1 ||
					panelHeightRatio <= 0
				) {
					console.warn(`Indicator-to-panel-height ratio for indicator '${i.params.id}' is out of range. Using fallback.`);
					panelHeightRatio = 1;
				}

				this.yAxis.setScaleDomain({
					scaleUid: i.scaleUids[1], // Get Y-axis scale UID
					start: secondaryDomain.max / panelHeightRatio,
					stop: secondaryDomain.min
				});

				this.yAxis.setScaleRange({
					start: range.min,
					stop: range.max,
					scaleUid: i.scaleUids[1] // Get Y-axis scale UID
				});
			}
		}

		if (domain.max !== 0 &&	domain.min !== 0) {
			// On log scales, altering the scale's range here is the fastest way to enforce yaxis padding.
			// (linear scales will be done by expanding the actual domain within _getOverallDataRange)
			if (this.index === 0 && this.core.params.yAxisScale === 'log') {
				// Only add bottom padding if marker doesn't draw to bottom of chart
				if (!foundDrawToBottomMarker) range.max -= this.params.style.yAxis.padding.bottom;
				range.min += this.params.style.yAxis.padding.top;
			}
		}

		// Set range for primary Y-axis scale
		this.yAxis.setScaleRange({
			start: range.min,
			stop: range.max
		});

		// Update padding automatically or use panel styles
		this.size.padding.right = this._getPaddingRight();
		this.size.width = this.core.size.width - this.size.padding.left - this.size.padding.right;
	}

	/**
	 * Renders all axes within the panel
	 * @param {array} dateLabels
	 */
	renderAxes(dateLabels = {}) {
		this.yAxis.render(); // Horizontal grid lines will be rendered in this method
		this.grid.renderVerticalGrid(dateLabels);
		this.xAxis.render(dateLabels);
	}

	/** Render an overall panel border and/or selected edges */
	renderBorders() {
		const ctx = this.rootContext;
		const fullBorderOffset = 1;
		const { size } = this;
		const { padding } = size;
		const { style } = this.params;
		const width =
			size.width -
			(style.border.left.padding || style.border.default.padding) -
			(style.border.right.padding || style.border.default.padding) - 1;
		const height =
			size.height -
			fullBorderOffset -
			(style.border.top.padding || style.border.default.padding) -
			(style.border.bottom.padding || style.border.default.padding);
		const left =
			padding.left +
			(style.border.left.padding || style.border.default.padding);
		const top =
			padding.top +
			(style.border.top.padding || style.border.default.padding);
		let color;
		let lineDash;
		let lineWidth;

		// render full border rectangle
		if (style.border.default.color) {
			ctx.save();
			ctx.beginPath();

			ctx.lineWidth = style.border.default.width;
			ctx.strokeStyle = style.border.default.color;
			ctx.setLineDash(style.border.default.lineDash.split(/\s*,\s*/));
			subpixel.rect(ctx, left, top, width, height);
			ctx.stroke();

			ctx.restore();
		}

		// render left border line
		color =
			style.border.left.color ||
			style.border.default.color;
		lineDash =
			style.border.left.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.left.width ||
			style.border.default.width;
		this.grid.renderVerticalLines(ctx, [subpixel.px(left)], top, height, color, lineDash, lineWidth);

		// render right border line
		color =
			style.border.right.color ||
			style.border.default.color;
		lineDash =
			style.border.right.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.right.width ||
			style.border.default.width;
		this.grid.renderVerticalLines(ctx, [subpixel.px(left + width)], top, height, color, lineDash, lineWidth);

		// render top border line
		color =
			style.border.top.color ||
			style.border.default.color;
		lineDash =
			style.border.top.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.top.width ||
			style.border.default.width;
		this.grid.renderHorizontalLines(ctx, [subpixel.px(top)], left, width, color, lineDash, lineWidth);

		// render bottom border line
		color =
			style.border.bottom.color ||
			style.border.default.color;
		lineDash =
			style.border.bottom.lineDash ||
			style.border.default.lineDash;
		lineWidth =
			style.border.bottom.width ||
			style.border.default.width;
		this.grid.renderHorizontalLines(ctx, [subpixel.px(top + height)], left, width, color, lineDash, lineWidth);
	}

	/**
	 * Renders session segments on the chart with appropriate styles and emits an event for further customization.
	 * @param {string} sessionType - Type of the session to render. Defaults to 'SessionBreak'.
	 */
	renderSessions(sessionType = 'SessionBreak') {
		if (Object.keys(this.sessionClips).length) {
			// Determine the style based on the session type
			const style = this.params.style[sessionType.toLowerCase()] || this.params.style.sessionBreak;

			// Context and panel dimensions
			const ctx = this.rootContext;
			const panelHeight = this.size.height;
			const panelWidth = this.size.width;
			const regions = []; // To store calculated session regions

			// Extract session clips based on exchange ID and session type
			const symbol = this.indicators[0].params.symbol || this.params.symbol; // the indicator symbol can sometimes be different (ARMS index, CRS)
			const exchangeId = this.core.xref[symbol]?.exchangeId;
			let sessionClips = [];

			if (exchangeId && this.sessionClips[exchangeId] && this.sessionClips[exchangeId][sessionType]) {
				sessionClips = this.sessionClips[exchangeId][sessionType] || [];
			}

			sessionClips.forEach(region => {
				const borderWidth = style.border.width;
				// Adjusting region bounds considering border width
				const sessionLeft = Math.max(region.left + Math.ceil(borderWidth / 2), 0);
				const sessionRight = Math.min(region.left + region.width - Math.ceil(borderWidth / 2), panelWidth);

				// Render side borders if defined
				if (style.border.color) {
					const { color: borderColor, lineDash: borderLineDash } = style.border;

					// Draw left border
					if (sessionLeft > 0) {
						this._renderSessionBorder(
							ctx, [sessionLeft, 0], [sessionLeft, panelHeight],
							borderColor, borderWidth, borderLineDash
						);
					}

					// Draw right border
					if (sessionRight < panelWidth) {
						this._renderSessionBorder(
							ctx, [sessionRight, 0], [sessionRight, panelHeight],
							borderColor, borderWidth, borderLineDash
						);
					}
				}

				// Fill session region with background color if defined
				if (style.background.color) {
					ctx.fillStyle = style.background.color;
					ctx.fillRect(sessionLeft, 0, sessionRight - sessionLeft, panelHeight);
				}

				// Store region information if it's within the panel width
				if (sessionRight > 0 && sessionLeft < panelWidth) {
					const midX = (sessionLeft + sessionRight) / 2;
					const midY = panelHeight / 2;

					regions.push({
						left: sessionLeft,
						right: sessionRight,
						top: 0,
						bottom: panelHeight,
						width: sessionRight - sessionLeft,
						height: panelHeight,
						midpoint: { x: midX, y: midY }
					});
				}
			});

			// Emit a SESSION_RENDER event with session details for further customization
			if (regions.length) {
				this.core.eventEmitter.emit(eventTypes.SESSION_RENDER, {
					regions, ctx, exchangeId, isUpper: this.isUpper, sessionType
				});
			}
		}
	}

	/**
	 * Renders a vertical session break line
	 */
	_renderSessionBorder(
		ctx,
		start = [0, 0],
		end = [0, 0],
		color = '#000',
		lineWidth = 1.5,
		lineDash = ''
	) {
		if (!lineWidth) return; // Exit early if lineWidth is 0 or null

		ctx.beginPath();
		ctx.lineJoin = 'round';
		ctx.lineWidth = lineWidth || this.params.lineWidth;
		ctx.strokeStyle = color || this.params.lineColor;

		ctx.moveTo(subpixel.px(start[0]), subpixel.px(start[1]));
		ctx.lineTo(subpixel.px(end[0]), subpixel.px(end[1]));

		if (ctx.setLineDash) {
			const dashArray = lineDash ? lineDash.split(/\s*,\s*/) : [];
			ctx.setLineDash(dashArray);
		}

		ctx.stroke();

		// Reset dashed line setting to default
		if (ctx.setLineDash) ctx.setLineDash([]);
	}

	/** fillText with a glow behind it */
	glowText(ctx, labelText, x, y, textColor, glowColor, glowWidth = 2) {
		// background
		if (
			typeof glowColor === 'string' &&
			glowColor.length &&
			glowColor !== 'none'
		) {
			ctx.strokeStyle = glowColor;
			ctx.lineWidth = glowWidth;
			ctx.strokeText(labelText, x, y);
		}

		// foreground
		ctx.fillStyle = textColor;
		ctx.lineWidth = 1;
		ctx.fillText(labelText, x, y);
	}

	/**
	 * remove from indicators collection
	 */
	removeIndicator(indicator) {
		// final cleanup before removal
		indicator.remove();

		// remove from panel's indicators collection
		for (let x = this.indicators.length - 1; x >= 0; x--) {
			if (indicator === this.indicators[x]) {
				this.indicators.splice(x, 1);
				break;
			}
		}
		this.updateLegend();
		const event = this.isUpper ? eventTypes.INDICATOR_UPPER_REMOVE : eventTypes.INDICATOR_LOWER_REMOVE;
		this.core.eventEmitter.emit(event, {
			indicator,
			indicatorType: indicator.params.id
		});
	}

	/**
	 * Remove from events collection by event object, event id, or event uid
	 */
	removeEvent(id, uid) {
		let type;
		let removedEventParams;
		// support either string or object for backward compatibilty
		if (typeof id === 'object' && id.params) {
			type = id.params.id;
			uid = id.params.uid;
		} else {
			type = id;
		}
		// remove matching events
		for (let x = this.events.length - 1; x >= 0; x--) {
			if (type === this.events[x].params.id) {
				if (!uid || this.events[x].params.uid === uid) {
					removedEventParams = { ...this.events[x].params };
					this.events[x].remove();
					this.events.splice(x, 1);
				}
			}
		}
		if (removedEventParams) {
			this.core.eventEmitter.emit(eventTypes.EVENT_REMOVE, {
				eventType: type,
				name: removedEventParams.name,
				uid: removedEventParams.uid
			});
			this.core.render();
		} else {
			this.core.warn(`Could not remove event ${type} ${uid}`);
		}
	}

	/**
	 * Remove from events collection by event name
	 */
	removeEventByName(name) {
		let removedEventParams = {};
		// remove matching events
		for (let x = this.events.length - 1; x >= 0; x--) {
			if (this.events[x].params.name === name) {
				removedEventParams = { ...this.events[x].params };
				this.events[x].remove();
				this.events.splice(x, 1);
			}
		}
		this.core.eventEmitter.emit(eventTypes.EVENT_REMOVE, {
			eventType: removedEventParams.id,
			name: removedEventParams.name,
			uid: removedEventParams.uid
		});
		this.core.render();
	}

	/**
	 * remove all tools from this panel
	 */
	removeTools() {
		for (let x = this.tools.length - 1; x >= 0; x--) {
			this.tools[x].remove();
		}

		this.core.render();
	}

	/** emit an event for the panel's legend element to be updated */
	updateLegend() {
		const legendElement = this.rootLegend.node();
		const { indicators } = this;
		const lastClose = {};

		indicators.forEach(indicator => {
			const { symbol, id, uid } = indicator.params;
			if (id && id === 'price' && this.core.data[symbol] && this.core.data[symbol][id] && this.core.data[symbol][id][uid]) {
				const dataSeries = this.core.data[symbol][id][uid];
				if (dataSeries && dataSeries.length) {
					lastClose[symbol] = dataSeries[dataSeries.length - 1].close;
				}
			}
		});

		try {
			this.core.eventEmitter.emit(eventTypes.PANEL_LEGEND_UPDATE, {
				panel: this,
				legendElement,
				indicators,
				lastClose
			});
		} catch (error) {
			console.error('Error emitting PANEL_LEGEND_UPDATE event:', error);
		}
	}

	/**
	 * hide panel's legend dom element
	 */
	concealLegend() {
		this.rootLegend.style('visibility', 'hidden');
	}

	/**
	 * reveal panel's legend dom element
	 */
	revealLegend() {
		if (this.rootLegend.style('display') !== 'none') {
			this.rootLegend.style('visibility', 'visible');
		}
	}

	/**
	 * @method onClickRemoveLegendIndicator
	 * Deprecated.  A wrapper for removeLegendIndicator.  Used for backwards-compatibility.
	 */
	onClickRemoveLegendIndicator(arg, options = {}) {
		return this.removeLegendIndicator(arg, options);
	}

	/**
	 * @method removeLegendIndicator
	 * This is a convenience method around removeIndicator() that performs extra cleanup
	 * including selectively removing lower panels and loading new data.
	 * call this method with one of:
	 *    - indicator object reference
	 *    - an indicator UID
	 *    - a d3 selector that points to an element with an "idx" and "panelId" attribute
	 */
	removeLegendIndicator(arg, options = {}) {

		const core = this.core;
		let loadData = false;
		let removeCompare = false;
		const overlays = [];
		let panel = {};
		let indicator;

		// argument was indicator reference
		if (arg.params && arg.params.uid) {
			indicator = arg;
		} else if (typeof arg === 'string') {
			// argument was an indicator uid
			indicator = core.getIndicatorByUid(arg);
		}

		if (indicator) {
			panel = indicator.panel;
		} else {
			// argument was a d3 selector

			const el = select(arg);
			const idx = el.attr('idx');

			panel = core.getPanelByUid(el.attr('panelId'));

			if (panel) {
				indicator = panel.indicators[idx];
			}
		}

		if (!indicator) {
			this.core.warn('Indicator reference not found.');
			return;
		}

		// check if this is a lower indicator with overlays
		for (let x = 1; x < panel.indicators.length; x++) {
			if (
				panel.indicators[x].params.parentUid === indicator.params.uid ||
				panel.indicators[x].params.parentUID === indicator.params.uid
			) {
				overlays.push(panel.indicators[x]);
			}
		}

		// special handling for price markers
		if (indicator.params.id === 'price' && indicator.params.symbol) {
			// always reload data after deleting a symbol.  this helps restore a chart if it just broke due to adding a bad symbol,
			// and forces the domain to be reset in case the deleted symbol was on a different exchange.
			loadData = true;

			// remove from symbolCompare collection (Deprectated CH-1504)
			const symbolIndex = panel.core.params.symbolCompare.indexOf(indicator.params.symbol);
			if (symbolIndex !== -1) {
				panel.core.params.symbolCompare.splice(symbolIndex, 1);
				removeCompare = true;
			}

			// detect removal of a comparison
			if (this.core.comparisons.find(i => i.params.symbol === indicator.params.symbol)) {
				removeCompare = true;
			}
		}

		// always reload data after deleting a sectorindustry
		if (indicator.params.id === 'sectorindustry') loadData = true;

		// remove indicator object
		this.removeIndicator(indicator);

		// selectively reset the domain if removing the last remaining ichimoku cloud
		if (
			indicator.params.id === 'ichimokucloud' &&
			core.getIndicatorsById('ichimokucloud').length === 0
		) {
			core.resetDomain();
			const defaultDomain = core.getDefaultDomain();
			core._updateXAxisScaleDomains(defaultDomain[0], defaultDomain[1]);
		}

		// remove the entire panel if that was its last non-overlay indicator
		if (this.indicators.length === 0 || overlays.length > 0) {
			core.removePanel(this);
			core.resize();
		} else if (removeCompare) {
			core.resetDomain();

			if (core.params.dateStart && core.params.dateStop) {
				const daysRange = Math.ceil(
					(core.params.dateStop.getTime() -
						core.params.dateStart.getTime()) /
						1000 /
						60 /
						60 /
						24
				);

				core.params.days = Math.min(
					core.state.isIntraday
						? core.limitIntradayTrading
						: core.params.rulerInterdayStart,
					daysRange
				);
			}

			this.updateLegend();
		} else {
			// update legend immediately
			this.updateLegend();
		}

		// load new data or render
		if (loadData && !options.noLoad) {
			core.loadData();
		} else {
			core.render();
		}
	}

	/**
	 * render error message on top of a panel
	 * @param {string} message
	 */
	renderErrorMessage(message) {
		this.rootError
			.html(message)
			.style('display', null);
	}

	/**
	 * Clear out the error message
	 */
	clearErrorMessage() {
		this.rootError.html('');
	}

	/**
	 * convert a normalized close value to an actual
	 */
	normalizedToActual(val) {
		if (
			this.core.params.normalizeValues &&
			this.core.params.normalizeValues.Close
		) {
			const normalizedValue = this.core.params.normalizeValues.Close;
			const valOrig = normalizedValue || 0;
			return (val / 100) * valOrig + valOrig;
		}

		return val;
	}

	/**
	 * convert an actual value to normalized
	 * see also: https://wiki.wsod.local/wsodwiki/index.php/Percent_Change_Display_Policy
	 */
	actualToNormalized(val) {
		let normalizedValue = 0;

		if (this.core.params.normalizeValues) {
			normalizedValue = this.core.params.normalizeValues.Close;
		}

		const valOrig = normalizedValue || this.core.params.normalizeValue;

		return ((val - valOrig) / valOrig) * 100;
	}

	/** assemble font-related styles into a single CSS font string.  Used by X and Y axis during renders */
	getFontStyle(fontWeight = 'normal', fontSize = 11, fontFamily = 'Arial, Helvetica, sans-serif', fontStyle) {
		// pull apart comma-delimited family list
		let newFamily = fontFamily.split(/,\s*/);

		// wrap quotes around each family individually - this is needed so certain families
		// with multiple spaces don't break the font string, such as "Gotham Narrow Ssm 5r"
		newFamily = newFamily.map(f => `"${f.replace(/["']/g, '')}"`);

		// assemble style, weight, size and family
		return [fontStyle, fontWeight, `${fontSize}px`, newFamily].join(' ').trim();
	}
	/**
	 * Gets generic object representation of Panel object
	 * @method toJSON
	 */
	toJSON() {
		const json = {
			params: { ...this.params },
			indicators: [],
			tools: [],
			events: [],
			size: {}
		};
		// saved indicators
		const indicators = this.indicators || [];
		for (let i = 0; i < indicators.length; i++) {
			const indicator = this.indicators[i];
			const indicatorJSON = indicator.toJSON();

			if (indicatorJSON) {
				json.indicators.push(indicatorJSON);
			}
		}
		// saved tools & handles
		const tools = this.tools || [];
		for (let t = 0; t < tools.length; t++) {
			const tool = this.tools[t];
			const toolJSON = tool.toJSON();
			json.tools.push(toolJSON);
		}
		// saved events
		const events = this.events || [];
		for (let e = 0; e < events.length; e++) {
			const eventParams = this.events[e].params;

			if (eventParams.id !== 'custom') {
				json.events.push({
					params: eventParams
				});
			}
		}

		// heightPct
		if (this.size && typeof this.size.heightPct === 'number') {
			json.params.size = json.params.size || {};
			json.params.size.heightPct = this.size.heightPct;
		}

		return json;
	}

	/**
	 * static map from id to Class name
	 * @returns {string}
	 */
	static getIndicatorClassName(id) {
		return Indicators.get(id) || null;
	}

	/**
	 * Rebuild the panel style/size params
	 * @param {*} oldStyles - Styles previously stored in via chart.params.style before the update
	 * @param {*} oldStyleMap - Chart style map from before the update
	 * @param {*} doSoftUpdate - Flag that enables/disables check for if invidual components have
	 * been altered from oldStyles/default styles
	 */
	updateStyles(oldStyles = {}, oldStyleMap = {}, doSoftUpdate = true) {
		function updatePanelStyles(panelObj) {
			updateComponentStyles(
				[panelObj],
				doSoftUpdate,
				() => merge({}, oldStyleMap.panel, oldStyles.panel || {}),
				panel => panel.getDefaultParams().style,
				panel => panel.params,
				[
					{ test: panel => panel.flags, updater: updateComponentFlagStyles }
				]
			);

			// Update extended hours marker styles (one of these lives on each panel if that option is enabled)
			if (panelObj.extendedHoursMarker) {
				updateComponentStyles(
					[panelObj.extendedHoursMarker],
					doSoftUpdate,
					() => merge({}, oldStyleMap.marker.extendedHours, get(oldStyles, 'marker.extendedHours') || {}),
					marker => marker.getDefaultParams().style,
					marker => marker.params
				);
			}

			// Update radius attribute of crosshair circle SVG
			if (panelObj.crosshairCircle) panelObj.crosshairCircle.attr('r', panelObj.params.style.crosshair.circleRadius);

			// Update panel size
			panelObj.params.size = merge({}, panelObj.getDefaultParams().size);
			panelObj.initPanelSize();
		}

		function updateComponentFlagStyles(component) {
			updateComponentStyles(
				component.flags,
				doSoftUpdate,
				flag => merge({}, get(oldStyleMap, `flag.${flag.id}`), get(oldStyles, `flag.${flag.id}`) || {}),
				flag => flag._getStyle(),
				flag => flag
			);
		}

		function updatePanelToolStyles(panel) {
			updateComponentStyles(
				panel.tools,
				doSoftUpdate,
				tool => merge({}, get(oldStyleMap, `tool.${tool.params.id}`), get(oldStyles, `tool.${tool.params.id}`) || {}),
				tool => tool._getDefaultParams().style,
				tool => tool.params,
				[
					{ test: tool => tool.flags, updater: updateComponentFlagStyles },
					{ test: tool => tool.handle.length, updater: updateToolHandleStyles }
				]
			);
		}

		function updatePanelEventStyles(panel) {
			updateComponentStyles(
				panel.events,
				doSoftUpdate,
				() => merge({}, get(oldStyleMap, 'event'), get(oldStyles, 'event') || {}),
				event => event._getParams().style,
				event => event.params
			);
		}

		function updatePanelIndicatorStyles(panel) {
			updateComponentStyles(
				panel.indicators,
				doSoftUpdate,
				indicator => merge({}, get(oldStyleMap, `indicator['${indicator.params.id}']`), get(oldStyles, `indicator['${indicator.params.id}']`) || {}),
				indicator => indicator._getDefaultStyles(),
				indicator => indicator.params,
				[
					{ test: indicator => indicator.flags, updater: updateComponentFlagStyles }
				]
			);
		}

		function updateToolHandleStyles(tool) {
			updateComponentStyles(
				tool.handle,
				doSoftUpdate,
				() => merge({}, get(oldStyleMap, 'tool.handle'), get(oldStyles, 'tool.handle') || {}),
				() => tool.core.getStyle('tool.handle'),
				handle => handle.params
			);
		}

		updatePanelStyles(this);
		updatePanelToolStyles(this);
		updatePanelEventStyles(this);
		updatePanelIndicatorStyles(this);
	}

	/** Returns the first panel indicator, if it exists. This should only be used for debugging. */
	get i0() {
		return this.indicators.length > 0
			? this.indicators[0]
			: null;
	}

	/** Returns the first panel tool, if it exists. This should only be used for debugging. */
	get t0() {
		return this.tools.length > 0
			? this.tools[0]
			: null;
	}

	/** Returns the first panel event, if it exists. This should only be used for debugging. */
	get e0() {
		return this.events.length > 0
			? this.events[0]
			: null;
	}

	get index() {
		return this.core.panels.indexOf(this);
	}

	/** Decreases panel index by 1 and re-renders. */
	moveUp() {
		this._moveIndex(-1);
		this.core.resize();
		this.core.render();
	}

	/** Increases panel index by 1 and re-renders. */
	moveDown() {
		this._moveIndex(1);
		this.core.resize();
		this.core.render();
	}

	/**
	 * Moves this panel's index up or down in the panels array, and ensures the DOM order
	 * matches the new index order (this is important for keyboard tab focus ordering)
	 * @param {number} indexDelta -1 or 1
	 * @private
	 */
	_moveIndex(indexDelta) {
		const panelIndex = this.index;
		const panels = this.core.panels;
		const newIndex = panelIndex + indexDelta;
		const chartEl = this.core.rootModchart.node();
		const panelEl = this.rootPanel.node();
		// error check
		if (typeof panels[newIndex] === 'undefined') {
			return this.core.warn('Panel move limit reached');
		}
		// reorder DOM
		if (indexDelta === -1) { // up
			chartEl.insertBefore(panelEl, panelEl.previousSibling);
		} else { // down
			chartEl.insertBefore(panelEl.nextSibling, panelEl);
		}
		// reorder panels array
		const thisPanel = panels.splice(panelIndex, 1)[0];
		panels.splice(newIndex, 0, thisPanel);
	}
}
